import { useEffect, useRef, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { Bell, CheckCircle2, Plus, Save, Trash2, Upload } from 'lucide-react';
import { db } from '../../lib/firebase';
import { compressImage } from '../../lib/images';
import { enablePushOnThisDevice } from '../../lib/push';
import { useSiteData } from '../../context/SiteDataContext';
import { Field } from './adminUi';
import { DAY_KEYS, DAY_LABELS, type SiteSettings, type TravelMode, type TravelRegion } from '../../lib/types';
import { fsaInfo } from '../../lib/travel';

export default function SettingsAdmin() {
  const { settings } = useSiteData();
  const [form, setForm] = useState<SiteSettings>(settings);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const heroFileInput = useRef<HTMLInputElement>(null);
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroError, setHeroError] = useState('');
  const [pushState, setPushState] = useState<'idle' | 'working' | 'enabled'>('idle');
  const [pushError, setPushError] = useState('');

  // Adopt live settings once they stream in (unless mid-edit after a save).
  useEffect(() => {
    setForm(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(settings)]);

  const save = async () => {
    setState('saving');
    try {
      await setDoc(doc(db(), 'settings', 'site'), form, { merge: true });
      setState('saved');
      setTimeout(() => setState('idle'), 2500);
    } catch {
      setState('error');
    }
  };

  const upd = (patch: Partial<SiteSettings>) => setForm({ ...form, ...patch });

  return (
    <div className="mx-auto max-w-4xl pb-24">
      <h1 className="font-display text-2xl font-bold text-white">Site Settings</h1>
      <p className="mt-1 text-sm text-zinc-500">
        Business info, homepage text, hours, and booking rules — changes go live the moment you save.
      </p>

      <section className="card mt-6 grid gap-4 p-6 sm:grid-cols-2">
        <h2 className="font-display font-bold text-white sm:col-span-2">Business Info</h2>
        <Field label="Business name">
          <input className="input" value={form.businessName} onChange={(e) => upd({ businessName: e.target.value })} />
        </Field>
        <Field label="Tagline">
          <input className="input" value={form.tagline} onChange={(e) => upd({ tagline: e.target.value })} />
        </Field>
        <Field label="Phone">
          <input className="input" value={form.phone} onChange={(e) => upd({ phone: e.target.value })} />
        </Field>
        <Field label="Email">
          <input className="input" value={form.email} onChange={(e) => upd({ email: e.target.value })} />
        </Field>
        <Field label="City">
          <input className="input" value={form.city} onChange={(e) => upd({ city: e.target.value })} />
        </Field>
        <Field label="Service area blurb">
          <input className="input" value={form.serviceArea} onChange={(e) => upd({ serviceArea: e.target.value })} />
        </Field>
      </section>

      <section className="card mt-6 p-6">
        <h2 className="font-display font-bold text-white">Mobile Service & Travel Fees</h2>
        <p className="mt-1 text-xs text-zinc-500">
          Controls the "we come to you" option and the postal-code checker on the homepage and in
          the booking flow.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Mobile service">
            <select
              className="input"
              value={form.travel.mode}
              onChange={(e) => upd({ travel: { ...form.travel, mode: e.target.value as TravelMode } })}
            >
              <option value="off">Off — shop drop-off only</option>
              <option value="flat">Anywhere — one flat travel fee</option>
              <option value="regions">Only my service regions (center + radius)</option>
            </select>
          </Field>
          {form.travel.mode === 'flat' && (
            <Field label="Flat travel fee (CAD, 0 = free)">
              <input
                className="input"
                type="number"
                min={0}
                value={form.travel.flatFee}
                onChange={(e) => upd({ travel: { ...form.travel, flatFee: Number(e.target.value) } })}
              />
            </Field>
          )}
        </div>

        {form.travel.mode === 'regions' && (
          <div className="mt-4">
            <label className="label">
              Service regions — customers must be within the radius of a region's center postal code
            </label>
            <div className="space-y-2.5">
              {form.travel.regions.map((r, i) => {
                const setRegion = (patch: Partial<TravelRegion>) => {
                  const regions = [...form.travel.regions];
                  regions[i] = { ...r, ...patch };
                  upd({ travel: { ...form.travel, regions } });
                };
                const center = fsaInfo(r.centerPostal);
                return (
                  <div key={r.id} className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
                    <div className="grid gap-2.5 sm:grid-cols-[1fr_130px_110px_110px_36px]">
                      <div>
                        <label className="label !mb-1 !text-[10px]">Region name</label>
                        <input
                          className="input"
                          value={r.name}
                          placeholder="e.g. Ottawa"
                          onChange={(e) => setRegion({ name: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="label !mb-1 !text-[10px]">Center postal</label>
                        <input
                          className="input uppercase"
                          value={r.centerPostal}
                          placeholder="K1P"
                          maxLength={7}
                          onChange={(e) => setRegion({ centerPostal: e.target.value.toUpperCase() })}
                        />
                      </div>
                      <div>
                        <label className="label !mb-1 !text-[10px]">Radius (km)</label>
                        <input
                          className="input"
                          type="number"
                          min={1}
                          max={500}
                          value={r.radiusKm}
                          onChange={(e) => setRegion({ radiusKm: Number(e.target.value) })}
                        />
                      </div>
                      <div>
                        <label className="label !mb-1 !text-[10px]">Fee (CAD)</label>
                        <input
                          className="input"
                          type="number"
                          min={0}
                          value={r.fee}
                          onChange={(e) => setRegion({ fee: Number(e.target.value) })}
                        />
                      </div>
                      <button
                        className="glass flex items-center justify-center self-end rounded-lg py-2.5 text-zinc-400 hover:text-red-400"
                        title="Remove region"
                        onClick={() =>
                          upd({ travel: { ...form.travel, regions: form.travel.regions.filter((_, j) => j !== i) } })
                        }
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    <p className={`mt-2 text-xs ${center ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {center
                        ? `✓ Center: ${center.area} (${center.fsa}) — serving everything within ${r.radiusKm} km`
                        : r.centerPostal.trim()
                          ? '✗ Unknown postal code — use a valid Canadian code like K1P or K2P 1L4'
                          : 'Enter the postal code at the center of this region (your shop, or downtown)'}
                    </p>
                  </div>
                );
              })}
            </div>
            <button
              className="btn-ghost mt-3 !px-4 !py-2 text-xs"
              onClick={() =>
                upd({
                  travel: {
                    ...form.travel,
                    regions: [
                      ...form.travel.regions,
                      { id: `region-${Date.now()}`, name: '', centerPostal: '', radiusKm: 25, fee: 45 } as TravelRegion,
                    ],
                  },
                })
              }
            >
              <Plus size={13} /> Add Region
            </button>
            <div className="mt-4 grid gap-4">
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-sky-500"
                  checked={form.travel.allowOutsideRequests}
                  onChange={(e) => upd({ travel: { ...form.travel, allowOutsideRequests: e.target.checked } })}
                />
                Let people outside my regions send a booking request anyway (flagged for your review)
              </label>
              <Field label="Message shown for out-of-area postal codes">
                <textarea
                  className="input min-h-16"
                  value={form.travel.outsideMessage}
                  onChange={(e) => upd({ travel: { ...form.travel, outsideMessage: e.target.value } })}
                />
              </Field>
            </div>
          </div>
        )}

        <div className="mt-5 grid gap-4 border-t border-white/[0.06] pt-5 sm:grid-cols-2">
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              className="h-4 w-4 accent-sky-500"
              checked={form.allowDropoff}
              onChange={(e) => upd({ allowDropoff: e.target.checked })}
            />
            Customers can drop off at our shop
          </label>
          <Field label="Shop address (shown on drop-off option)">
            <input className="input" value={form.shopAddress} onChange={(e) => upd({ shopAddress: e.target.value })} placeholder="123 Bank St, Ottawa, ON" />
          </Field>
        </div>
      </section>

      <section className="card mt-6 grid gap-4 p-6">
        <h2 className="font-display font-bold text-white">Homepage Text</h2>
        <Field label="Hero title (last word gets the color accent)">
          <input className="input" value={form.heroTitle} onChange={(e) => upd({ heroTitle: e.target.value })} />
        </Field>
        <Field label="Hero subtitle">
          <textarea className="input min-h-20" value={form.heroSubtitle} onChange={(e) => upd({ heroSubtitle: e.target.value })} />
        </Field>
        <Field label="Hero image (the big homepage photo)">
          <div className="flex gap-2.5">
            <input
              className="input flex-1"
              value={form.heroImage.startsWith('data:') ? '(uploaded photo)' : form.heroImage}
              onChange={(e) => upd({ heroImage: e.target.value })}
              placeholder="https://…/your-best-shot.jpg"
              disabled={form.heroImage.startsWith('data:')}
            />
            <input
              ref={heroFileInput}
              type="file"
              accept="image/*"
              hidden
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setHeroUploading(true);
                setHeroError('');
                try {
                  upd({ heroImage: await compressImage(file, 1920) });
                } catch (err: any) {
                  setHeroError(err?.message ?? 'Upload failed');
                } finally {
                  setHeroUploading(false);
                  if (heroFileInput.current) heroFileInput.current.value = '';
                }
              }}
            />
            <button className="btn-ghost !px-4 !py-2.5 text-sm" onClick={() => heroFileInput.current?.click()} disabled={heroUploading}>
              <Upload size={15} /> {heroUploading ? 'Working…' : 'Upload Photo'}
            </button>
            {form.heroImage.startsWith('data:') && (
              <button className="btn-ghost !px-4 !py-2.5 text-sm" onClick={() => upd({ heroImage: '' })}>
                Clear
              </button>
            )}
          </div>
        </Field>
        {heroError && <p className="-mt-2 text-sm text-red-400">{heroError}</p>}
        {form.heroImage && (
          <img
            src={form.heroImage}
            alt="Hero preview"
            className="max-h-40 w-full rounded-xl border border-white/10 object-cover"
          />
        )}
        <p className="-mt-2 text-xs text-zinc-500">
          Upload a photo (it's compressed automatically) or paste any image link. Wide landscape
          photos look best. Remember to hit Save Settings below.
        </p>
        <Field label='"Why Apex" about text'>
          <textarea className="input min-h-24" value={form.aboutText} onChange={(e) => upd({ aboutText: e.target.value })} />
        </Field>
      </section>

      <section className="card mt-6 grid gap-4 p-6">
        <h2 className="font-display font-bold text-white">Announcement Banner</h2>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" className="h-4 w-4 accent-sky-500" checked={form.announcementEnabled} onChange={(e) => upd({ announcementEnabled: e.target.checked })} />
          Show announcement bar at the top of the site
        </label>
        <Field label="Announcement text">
          <input className="input" value={form.announcementText} onChange={(e) => upd({ announcementText: e.target.value })} placeholder="Grand opening special — 15% off this month!" />
        </Field>
      </section>

      <section className="card mt-6 grid gap-4 p-6 sm:grid-cols-2">
        <h2 className="font-display font-bold text-white sm:col-span-2">Social Links (blank = hidden)</h2>
        {(['instagram', 'facebook', 'tiktok', 'youtube', 'google'] as const).map((key) => (
          <Field key={key} label={key === 'google' ? 'Google Business profile' : key[0].toUpperCase() + key.slice(1)}>
            <input
              className="input"
              value={form.socials[key]}
              placeholder={`https://${key === 'google' ? 'g.page' : key + '.com'}/…`}
              onChange={(e) => upd({ socials: { ...form.socials, [key]: e.target.value } })}
            />
          </Field>
        ))}
      </section>

      <section className="card mt-6 p-6">
        <h2 className="font-display font-bold text-white">Business Hours</h2>
        <div className="mt-4 space-y-2.5">
          {DAY_KEYS.map((d) => {
            const h = form.hours[d];
            return (
              <div key={d} className="flex flex-wrap items-center gap-3">
                <span className="w-24 text-sm font-medium text-zinc-300">{DAY_LABELS[d]}</span>
                <label className="flex items-center gap-1.5 text-xs text-zinc-400">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-sky-500"
                    checked={!h.closed}
                    onChange={(e) => upd({ hours: { ...form.hours, [d]: { ...h, closed: !e.target.checked } } })}
                  />
                  Open
                </label>
                {!h.closed && (
                  <>
                    <input type="time" className="input !w-32 [color-scheme:dark]" value={h.open} onChange={(e) => upd({ hours: { ...form.hours, [d]: { ...h, open: e.target.value } } })} />
                    <span className="text-zinc-600">to</span>
                    <input type="time" className="input !w-32 [color-scheme:dark]" value={h.close} onChange={(e) => upd({ hours: { ...form.hours, [d]: { ...h, close: e.target.value } } })} />
                  </>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="card mt-6 p-6">
        <h2 className="flex items-center gap-2 font-display font-bold text-white">
          <Bell size={17} className="text-accent-400" /> Booking Notifications
        </h2>
        <p className="mt-1 text-xs text-zinc-500">
          Get a push notification on this device the moment a booking or quote request comes in.
          One-time setup: Firebase console → Project settings → Cloud Messaging → Web Push
          certificates → <span className="text-zinc-300">Generate key pair</span> → paste the key
          here, save, then enable each device you want notified.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto]">
          <Field label="Web Push key (from Firebase console)">
            <input
              className="input"
              value={form.notifications.vapidKey}
              onChange={(e) => upd({ notifications: { vapidKey: e.target.value.trim() } })}
              placeholder="BNdVxy…"
            />
          </Field>
          <div className="flex items-end">
            <button
              className="btn-primary !py-2.5 text-sm"
              disabled={pushState === 'working' || !form.notifications.vapidKey}
              onClick={async () => {
                setPushError('');
                setPushState('working');
                try {
                  await enablePushOnThisDevice(form.notifications.vapidKey);
                  setPushState('enabled');
                } catch (e: any) {
                  setPushError(e?.message ?? 'Failed to enable notifications');
                  setPushState('idle');
                }
              }}
            >
              {pushState === 'working' ? 'Enabling…' : pushState === 'enabled' ? '✓ Enabled on this device' : 'Enable on This Device'}
            </button>
          </div>
        </div>
        {pushError && <p className="mt-2 text-sm text-red-400">{pushError}</p>}
      </section>

      <section className="card mt-6 grid gap-4 p-6 sm:grid-cols-2">
        <h2 className="font-display font-bold text-white sm:col-span-2">Online Booking Rules</h2>
        <Field label="First arrival slot (hour, 24h)">
          <input className="input" type="number" min={0} max={23} value={form.booking.slotStart} onChange={(e) => upd({ booking: { ...form.booking, slotStart: Number(e.target.value) } })} />
        </Field>
        <Field label="Last arrival slot (hour, 24h)">
          <input className="input" type="number" min={0} max={23} value={form.booking.slotEnd} onChange={(e) => upd({ booking: { ...form.booking, slotEnd: Number(e.target.value) } })} />
        </Field>
        <Field label="Slot spacing (hours)">
          <input className="input" type="number" min={1} max={6} value={form.booking.slotIntervalHours} onChange={(e) => upd({ booking: { ...form.booking, slotIntervalHours: Number(e.target.value) } })} />
        </Field>
        <Field label="How far out customers can book (days)">
          <input className="input" type="number" min={1} max={180} value={form.booking.daysAhead} onChange={(e) => upd({ booking: { ...form.booking, daysAhead: Number(e.target.value) } })} />
        </Field>
        <Field label="Minimum notice (hours)">
          <input className="input" type="number" min={0} max={168} value={form.booking.minLeadHours} onChange={(e) => upd({ booking: { ...form.booking, minLeadHours: Number(e.target.value) } })} />
        </Field>
      </section>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-white/[0.08] bg-night-900/95 p-4 backdrop-blur lg:left-64">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4">
          <span className="text-sm text-zinc-500">
            {state === 'saved' && (
              <span className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 size={15} /> Saved — live on the site.</span>
            )}
            {state === 'error' && <span className="text-red-400">Save failed — check that Firestore is set up.</span>}
          </span>
          <button className="btn-primary !py-2.5 text-sm" onClick={save} disabled={state === 'saving'}>
            <Save size={15} /> {state === 'saving' ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
